import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, ArrowRight, Check, AlertCircle, Plus, X, RefreshCw } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { TransferRequests } from '@/components/transfers/TransferRequests';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Transfers() {
  const [tab, setTab] = useState('incoming'); // incoming | outgoing | corrections | history | new
  const [myBranch, setMyBranch] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [incomingHistory, setIncomingHistory] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // {mode: 'receive'|'correct', transfer}
  const [allowNegative, setAllowNegative] = useState(false); // switch admin: transferir sin stock
  const [requestsCount, setRequestsCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/transfer-requests?direction=incoming`, { withCredentials: true })
      .then(r => setRequestsCount((r.data || []).length)).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const safe = (p) => p.then(r => r.data).catch(() => null);
      const [br, inc, incH, out, cor, brs, prods, neg] = await Promise.all([
        safe(axios.get(`${API}/auth/branch`, { withCredentials: true })),
        safe(axios.get(`${API}/transfers/incoming`, { withCredentials: true })),
        safe(axios.get(`${API}/transfers/incoming?include_history=true`, { withCredentials: true })),
        safe(axios.get(`${API}/transfers/outgoing`, { withCredentials: true })),
        safe(axios.get(`${API}/transfers/pending-corrections`, { withCredentials: true })),
        safe(axios.get(`${API}/branches/list`, { withCredentials: true })),
        safe(axios.get(`${API}/products?include_variants=true`, { withCredentials: true })),
        safe(axios.get(`${API}/config/allow-negative-stock-transfers`, { withCredentials: true })),
      ]);
      if (br) setMyBranch(br);
      setIncoming(inc || []);
      setIncomingHistory((incH || []).filter(t => t.status !== 'pending_reception'));
      setOutgoing(out || []);
      setCorrections(cor || []);
      setBranches(brs || []);
      setProducts(prods || []);
      setAllowNegative(!!(neg && neg.enabled));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Ref: recuerda el estado previo de las transferencias entrantes (arrival y approval req)
  // para detectar cambios y disparar toasts sin necesidad de recargar.
  const prevIncomingRef = useRef(new Map());

  // Cada vez que cambia `incoming`, comparar contra el snapshot previo y notificar
  useEffect(() => {
    const prev = prevIncomingRef.current;
    const next = new Map();
    for (const t of incoming) {
      const key = t.id;
      const prevRec = prev.get(key);
      const nowConfirmed = !!t.arrival_confirmed_at;
      const nowHasReq = !!t.arrival_approval_request_id;
      // Aprobación NUEVA por el admin → toast verde
      if (prevRec && !prevRec.confirmed && nowConfirmed) {
        toast.success(
          `🚚 ${t.folio}: admin confirmó que ya llegó la mercancía · ya puedes recibirla`,
          { duration: 8000 }
        );
      }
      // Solicitud pendiente que fue RESUELTA en negativo (approval request id se limpió sin confirm)
      // — no común; el backend actualmente no la limpia, así que omitimos.
      next.set(key, { confirmed: nowConfirmed, hasReq: nowHasReq });
    }
    prevIncomingRef.current = next;
  }, [incoming]);

  // Polling ligero para incoming: refresca cada 8s si hay transferencias por recibir
  // (funciona en cualquier pestaña de la página de Transferencias para no perder la
  // notificación cuando el admin apruebe la llegada por Telegram).
  useEffect(() => {
    if (incoming.length === 0) return;
    const iv = setInterval(() => {
      axios.get(`${API}/transfers/incoming`, { withCredentials: true })
        .then(r => setIncoming(r.data || []))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, [incoming.length]);

  const pendingCount = incoming.length + corrections.length;

  return (
    <MainLayout>
    <div className="p-4 max-w-5xl mx-auto" data-testid="transfers-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6" />
            Transferencias de mercancía
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {myBranch ? `Sucursal actual: ${myBranch.branch_name}` : '...'}
          </p>
        </div>
        <button
          onClick={loadAll}
          className="p-2 hover:bg-slate-100 rounded-lg"
          data-testid="transfers-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Banner de incoming urgente */}
      {incoming.length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4 flex items-start gap-3" data-testid="incoming-banner">
          <Package className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-amber-900">
              📦 Mercancía en camino a tu sucursal
            </div>
            <div className="text-sm text-amber-800">
              {incoming.length} transferencia{incoming.length !== 1 ? 's' : ''} pendiente{incoming.length !== 1 ? 's' : ''} de recepción
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto">
        {[
          { id: 'incoming', label: `📥 Por recibir`, count: incoming.length },
          { id: 'corrections', label: `⚠️ A corregir`, count: corrections.length },
          { id: 'outgoing', label: `📤 Enviadas`, count: outgoing.length },
          { id: 'history', label: `📚 Historial recibidas`, count: incomingHistory.length },
          { id: 'requests', label: `📝 Peticiones`, count: requestsCount },
          { id: 'new', label: `➕ Nueva`, count: 0 },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition ${
              tab === t.id ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label} {t.count > 0 && <span className="ml-1 bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'incoming' && (
        <TransferList
          transfers={incoming}
          emptyText="No hay transferencias por recibir"
          onOpen={(t) => setConfirmModal({ mode: 'receive', transfer: t })}
          actionLabel="Recibir"
          incomingMode
          onRequestArrival={async (t) => {
            try {
              await axios.post(`${API}/transfers/${t.id}/request-arrival`, {}, { withCredentials: true });
              toast.success('🚚 Solicitud enviada al admin por Telegram');
              loadAll();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'No se pudo enviar');
            }
          }}
        />
      )}
      {tab === 'corrections' && (
        <TransferList
          transfers={corrections}
          emptyText="No hay transferencias con errores por corregir"
          onOpen={(t) => setConfirmModal({ mode: 'correct', transfer: t })}
          actionLabel="Corregir"
          highlight
        />
      )}
      {tab === 'outgoing' && (
        <>
          <OutgoingFilters onFilter={(qs) => {
            axios.get(`${API}/transfers/outgoing${qs}`, { withCredentials: true })
              .then(({ data }) => setOutgoing(data || []))
              .catch(() => {});
          }} />
          <TransferList
            transfers={outgoing}
            emptyText="No has enviado ninguna transferencia todavía"
            onOpen={(t) => setSelectedTransfer(t)}
            actionLabel="Ver"
            onCancel={async (t) => {
              if (!window.confirm(`¿Cancelar transferencia ${t.folio}? El stock no se ha movido.`)) return;
              try {
                await axios.post(`${API}/transfers/${t.id}/cancel`, {}, { withCredentials: true });
                toast.success('Transferencia cancelada');
                loadAll();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Error al cancelar');
              }
            }}
          />
        </>
      )}
      {tab === 'history' && (
        <TransferList
          transfers={incomingHistory}
          emptyText="No hay historial de transferencias recibidas"
          onOpen={(t) => setSelectedTransfer(t)}
          actionLabel="Ver"
        />
      )}
      {tab === 'requests' && (
        <TransferRequests
          myBranch={myBranch}
          branches={branches}
          products={products}
          onCountChange={setRequestsCount}
          onFulfilled={loadAll}
        />
      )}
      {tab === 'new' && (
        <NewTransferForm
          myBranch={myBranch}
          branches={branches}
          products={products}
          allowNegative={allowNegative}
          onCreated={() => { loadAll(); setTab('outgoing'); }}
          onOpen={loadAll}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          mode={confirmModal.mode}
          transfer={confirmModal.transfer}
          onClose={() => setConfirmModal(null)}
          onDone={() => { setConfirmModal(null); loadAll(); }}
        />
      )}
      {selectedTransfer && (
        <DetailModal transfer={selectedTransfer} onClose={() => setSelectedTransfer(null)} />
      )}
    </div>
    </MainLayout>
  );
}

function OutgoingFilters({ onFilter }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [status, setStatus] = useState('');

  const apply = () => {
    const p = new URLSearchParams();
    if (start) p.set('start_date', start);
    if (end) p.set('end_date', end + 'T23:59:59');
    if (status) p.set('status', status);
    const qs = p.toString();
    onFilter(qs ? `?${qs}` : '');
  };
  const clear = () => { setStart(''); setEnd(''); setStatus(''); onFilter(''); };
  const exportCSV = () => {
    const p = new URLSearchParams({ direction: 'outgoing' });
    if (start) p.set('start_date', start);
    if (end) p.set('end_date', end + 'T23:59:59');
    window.open(`${API}/transfers/export.csv?${p.toString()}`, '_blank');
  };

  return (
    <div className="bg-white border rounded-xl p-3 mb-3 flex flex-wrap gap-2 items-end" data-testid="outgoing-filters">
      <div>
        <label className="block text-[10px] uppercase text-slate-500 mb-1">Desde</label>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="filter-start" />
      </div>
      <div>
        <label className="block text-[10px] uppercase text-slate-500 mb-1">Hasta</label>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="filter-end" />
      </div>
      <div>
        <label className="block text-[10px] uppercase text-slate-500 mb-1">Estado</label>
        <select value={status} onChange={e => setStatus(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="filter-status">
          <option value="">Todos</option>
          <option value="pending_reception">Pendiente</option>
          <option value="has_errors">Con errores</option>
          <option value="completed">Completada</option>
        </select>
      </div>
      <button onClick={apply} className="px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded text-sm font-medium" data-testid="filter-apply">
        Filtrar
      </button>
      <button onClick={clear} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 rounded text-sm">
        Limpiar
      </button>
      <button onClick={exportCSV} className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium" data-testid="filter-export">
        📥 Exportar CSV
      </button>
    </div>
  );
}

function TransferList({ transfers, emptyText, onOpen, actionLabel, highlight, onCancel, incomingMode, onRequestArrival }) {
  // Tick de 1s para actualizar los countdowns visualmente
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!incomingMode) return;
    const t = setInterval(() => forceTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [incomingMode]);

  if (transfers.length === 0) {
    return <div className="text-center py-12 text-slate-400 text-sm">{emptyText}</div>;
  }
  return (
    <div className="space-y-3">
      {transfers.map(t => {
        const canCancel = onCancel && (t.status === 'pending_reception' || t.status === 'has_errors');

        // Cooldown de aceptación (30 min desde creación) — sólo en incoming
        let cooldownActive = false;
        let cooldownLabel = '';
        if (incomingMode && t.status === 'pending_reception') {
          const arrivalOk = !!t.arrival_confirmed_at;
          if (!arrivalOk && t.acceptance_available_at) {
            const availMs = new Date(t.acceptance_available_at).getTime();
            const nowMs = Date.now();
            if (nowMs < availMs) {
              cooldownActive = true;
              const remaining = Math.max(0, Math.ceil((availMs - nowMs) / 1000));
              const mm = Math.floor(remaining / 60);
              const ss = remaining % 60;
              cooldownLabel = `${mm}:${String(ss).padStart(2, '0')}`;
            }
          }
        }

        const hasPendingArrivalReq = !!t.arrival_approval_request_id && !t.arrival_confirmed_at;

        return (
        <div
          key={t.id}
          className={`bg-white border rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition ${
            highlight ? 'border-rose-300 bg-rose-50' : 'border-slate-200'
          }`}
          data-testid={`transfer-card-${t.folio}`}
        >
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{t.folio}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap mt-0.5">
              <span className="font-medium">{t.from_branch_name}</span>
              <ArrowRight className="w-3 h-3" />
              <span className="font-medium">{t.to_branch_name}</span>
              <span>·</span>
              <span>{t.items?.length || 0} items · {t.total_qty} piezas</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {new Date(t.created_at).toLocaleString('es-MX')}
              {t.status === 'has_errors' && <span className="ml-2 text-rose-600 font-semibold">CON ERRORES</span>}
              {t.status === 'completed' && (t.had_errors || t.correction_count > 0)
                ? <span className="ml-2 text-amber-600 font-semibold" data-testid={`transfer-completed-errors-${t.id}`}>COMPLETADA (CON ERRORES)</span>
                : t.status === 'completed' && <span className="ml-2 text-emerald-600 font-semibold">COMPLETADA</span>}
              {t.status === 'pending_reception' && <span className="ml-2 text-amber-600 font-semibold">PENDIENTE</span>}
              {t.status === 'cancelled' && <span className="ml-2 text-slate-500 font-semibold">CANCELADA</span>}
            </div>
            {incomingMode && cooldownActive && (
              <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5 font-mono font-semibold">
                  ⏳ {cooldownLabel}
                </span>
                <span className="text-slate-500">para poder recibir · o pide aprobación si ya llegó</span>
              </div>
            )}
            {incomingMode && t.arrival_confirmed_at && (
              <div className="mt-1.5 text-[11px] text-emerald-700 font-semibold">
                ✅ Llegada aprobada por admin
              </div>
            )}
            {incomingMode && hasPendingArrivalReq && !t.arrival_confirmed_at && (
              <div className="mt-1.5 text-[11px] text-sky-700 font-semibold">
                📨 Solicitud de aprobación enviada al admin — esperando respuesta…
              </div>
            )}
          </div>
          {canCancel && (
            <button
              onClick={() => onCancel(t)}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-slate-200 hover:bg-rose-200 text-slate-700 hover:text-rose-800"
              data-testid={`transfer-cancel-${t.folio}`}
              title="Cancelar transferencia"
            >Cancelar</button>
          )}
          {incomingMode && cooldownActive && !hasPendingArrivalReq && (
            <button
              onClick={() => onRequestArrival && onRequestArrival(t)}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white"
              data-testid={`transfer-arrived-${t.folio}`}
              title="Ya llegó la mercancía — pedir aprobación al admin para saltar el cooldown"
            >🚚 Ya llegó · Pedir aprobación</button>
          )}
          <button
            onClick={() => !cooldownActive && onOpen(t)}
            disabled={cooldownActive}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              cooldownActive
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : highlight
                  ? 'bg-rose-600 hover:bg-rose-700 text-white'
                  : 'bg-sky-600 hover:bg-sky-700 text-white'
            }`}
            data-testid={`transfer-open-${t.folio}`}
            title={cooldownActive ? 'Espera al cooldown o pide aprobación de llegada' : ''}
          >
            {actionLabel}
          </button>
        </div>
      );})}
    </div>
  );
}

function ConfirmModal({ mode, transfer, onClose, onDone }) {
  const [items, setItems] = useState(
    transfer.items.map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      image_url: it.image_url,
      original_qty: it.quantity,
      received_qty: it.received_qty ?? it.quantity,
      has_error: false,
      error_note: '',
    }))
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const path = mode === 'receive' ? `confirm` : `correct`;
      await axios.post(`${API}/transfers/${transfer.id}/${path}`, {
        items: items.map(i => ({
          product_id: i.product_id,
          received_qty: parseInt(i.received_qty || 0, 10),
          has_error: !!i.has_error,
          error_note: i.error_note || null,
        }))
      }, { withCredentials: true });
      toast.success(mode === 'receive' ? 'Transferencia confirmada' : 'Corrección enviada');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al confirmar');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase">
              {mode === 'receive' ? 'Confirmar recepción' : 'Corregir transferencia'}
            </div>
            <div className="font-bold text-lg">{transfer.folio}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <span>{transfer.from_branch_name}</span>
              <ArrowRight className="w-3 h-3" />
              <span>{transfer.to_branch_name}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto p-4 space-y-3 flex-1">
          {items.map((it, idx) => (
            <div key={it.product_id} className="border rounded-lg p-3 flex gap-3">
              {it.image_url ? (
                <img src={it.image_url} alt="" className="w-16 h-16 object-cover rounded" />
              ) : (
                <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-slate-300">
                  <Package className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{it.product_name}</div>
                <div className="text-xs text-slate-500 mb-2">
                  {mode === 'receive' ? `Enviado: ${it.original_qty}` : `Cantidad original: ${it.original_qty}`}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-slate-600">
                    {mode === 'receive' ? 'Recibí:' : 'Nueva cantidad:'}
                    <input
                      type="number"
                      min="0"
                      value={it.received_qty}
                      onChange={e => updateItem(idx, { received_qty: e.target.value })}
                      className="ml-1 w-20 px-2 py-1 border rounded text-sm"
                      data-testid={`item-qty-${it.product_id}`}
                    />
                  </label>
                  {mode === 'receive' && (
                    <label className="text-xs text-slate-600 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={it.has_error}
                        onChange={e => updateItem(idx, { has_error: e.target.checked })}
                        data-testid={`item-error-${it.product_id}`}
                      />
                      Marcar como error
                    </label>
                  )}
                </div>
                {(it.has_error || mode === 'receive') && (
                  <input
                    type="text"
                    value={it.error_note}
                    onChange={e => updateItem(idx, { error_note: e.target.value })}
                    placeholder={it.has_error ? "Motivo del error..." : "Nota (opcional)"}
                    className="mt-2 w-full px-2 py-1 border rounded text-xs"
                    data-testid={`item-note-${it.product_id}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg text-sm"
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="confirm-transfer-btn"
          >
            {saving ? 'Guardando...' : mode === 'receive' ? 'Confirmar recepción' : 'Enviar corrección'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ transfer, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-bold text-lg">{transfer.folio}</div>
            <div className="text-xs text-slate-500">{transfer.from_branch_name} → {transfer.to_branch_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl">×</button>
        </div>
        <div className="p-4">
          <div className="text-xs text-slate-500 mb-2">Estado: <span className="font-semibold">{transfer.status}</span></div>
          {transfer.confirmed_at && (
            <div className="text-xs text-slate-500 mb-3">
              Confirmado {new Date(transfer.confirmed_at).toLocaleString('es-MX')} por {transfer.confirmed_by_name}
            </div>
          )}
          <div className="space-y-2">
            {transfer.items.map(it => (
              <div key={it.product_id} className="border rounded p-2 flex items-center gap-2">
                {it.image_url ? <img src={it.image_url} alt="" className="w-12 h-12 rounded object-cover" /> : null}
                <div className="flex-1">
                  <div className="text-sm">{it.product_name}</div>
                  <div className="text-xs text-slate-500">
                    Enviado: {it.quantity}
                    {it.received_qty != null && it.received_qty !== it.quantity && ` · Recibido: ${it.received_qty}`}
                    {it.has_error && <span className="ml-1 text-rose-600">⚠ {it.error_note || 'error'}</span>}
                  </div>
                </div>
                {it.confirmed && !it.has_error && <Check className="w-4 h-4 text-emerald-600" />}
                {it.has_error && <AlertCircle className="w-4 h-4 text-rose-600" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewTransferForm({ myBranch, branches, products, allowNegative, onCreated, onOpen }) {
  const [destino, setDestino] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [productFilter, setProductFilter] = useState('');

  // Al montar (o cambiar de tab a "new"), pedir productos frescos al padre
  useEffect(() => { if (onOpen) onOpen(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const otherBranches = branches.filter(b => b.id !== myBranch?.branch_id);
  useEffect(() => {
    if (!destino && otherBranches[0]) setDestino(otherBranches[0].id);
  }, [otherBranches, destino]);

  const availableProducts = products.filter(p =>
    !p.parent_product_id &&
    p.visible_in_pos !== false &&
    (!productFilter || (p.name?.toLowerCase().includes(productFilter.toLowerCase()) || p.sku?.toLowerCase().includes(productFilter.toLowerCase())))
  );

  const addItem = (p) => {
    if (items.some(it => it.product_id === p.id)) return;
    if ((p.stock || 0) <= 0 && !allowNegative) {
      toast.error(`No tienes stock de "${p.name}" en esta sucursal. Activa "Permitir transferir sin existencias" en Configuración si quieres transferirlo de todos modos.`);
      return;
    }
    // Si allowNegative está ON, permitimos aunque stock sea 0. `max` queda como null
    // para no limitar la cantidad — el usuario decide cuánto transferir.
    const maxAllowed = allowNegative ? null : p.stock;
    setItems(prev => [...prev, { product_id: p.id, product_name: p.name, image_url: p.image_url, sku: p.sku, quantity: 1, max: maxAllowed }]);
    setProductFilter('');
  };

  const submit = async () => {
    if (!destino || items.length === 0) {
      toast.error('Elige destino y al menos un producto');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/transfers`, {
        to_branch_id: destino,
        items: items.map(i => ({ product_id: i.product_id, quantity: parseInt(i.quantity || 0, 10) })),
        notes: notes.trim() || null,
      }, { withCredentials: true });
      toast.success('Transferencia creada. Notificando destino...');
      setItems([]); setNotes('');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="new-transfer-form">
      <div className="bg-white border rounded-xl p-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Sucursal destino</label>
        <select
          value={destino}
          onChange={e => setDestino(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          data-testid="destino-select"
        >
          {otherBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded-xl p-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Agregar producto</label>
        <input
          value={productFilter}
          onChange={e => setProductFilter(e.target.value)}
          placeholder="Buscar por nombre o SKU..."
          className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
          data-testid="product-search"
        />
        {productFilter && (
          <div className="max-h-48 overflow-y-auto border rounded divide-y">
            {availableProducts.slice(0, 12).map(p => {
              const noStock = (p.stock || 0) <= 0;
              const canAdd = !noStock || allowNegative;
              return (
                <button
                  key={p.id}
                  onClick={() => addItem(p)}
                  disabled={!canAdd}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm ${noStock ? 'opacity-60' : ''} ${!canAdd ? 'cursor-not-allowed' : ''}`}
                  data-testid={`add-product-${p.id}`}
                >
                  {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className={`text-xs font-semibold ${noStock ? (allowNegative ? 'text-amber-600' : 'text-rose-500') : 'text-slate-500'}`}>
                    {noStock ? (allowNegative ? 'sin stock (permitido)' : 'sin stock') : `stock: ${p.stock}`}
                  </span>
                </button>
              );
            })}
            {availableProducts.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">Sin resultados</div>}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-600 mb-2">Items ({items.length})</div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it.product_id} className="flex items-center gap-2 border rounded p-2">
                {it.image_url && <img src={it.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{it.product_name}</div>
                  <div className="text-[10px] text-slate-400">
                    {it.max === null || it.max === undefined
                      ? 'Sin stock — se permitirá negativo'
                      : `Stock disponible: ${it.max}`}
                  </div>
                </div>
                <input
                  type="number"
                  min="1"
                  {...(it.max !== null && it.max !== undefined ? { max: it.max } : {})}
                  value={it.quantity}
                  onChange={e => setItems(prev => prev.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                  className="w-20 px-2 py-1 border rounded text-sm"
                  data-testid={`item-qty-input-${it.product_id}`}
                />
                <button onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))} className="text-rose-500 hover:text-rose-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl p-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Notas (opcional)</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Comentario para el destino..."
          className="w-full px-3 py-2 border rounded-lg text-sm"
          data-testid="transfer-notes"
        />
      </div>

      <button
        onClick={submit}
        disabled={saving || items.length === 0 || !destino}
        className="w-full py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-medium disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="submit-transfer"
      >
        <Plus className="w-5 h-5" />
        {saving ? 'Creando...' : 'Crear transferencia'}
      </button>
    </div>
  );
}
